/* eslint-disable no-console */
import React from 'react';
import PropTypes from 'prop-types';
import { reportError } from 'helpers/error-logging';
import { isRedirectingToLogin as readRedirectFlag } from 'helpers/session-auth-redirect';
import { dispatchNetworkErrorSnackbar, isChunkLoadError, isResizeObserverLoopError } from 'helpers/transport-error';
import InternalErrorComponent from '../InternalError';

const COMPONENT_STACK_FRAME = /^\s*(?:at |in )([A-Z][\w$]*)/;

function getComponentNameFromStack(componentStack) {
  const frame = componentStack?.split('\n').find(line => COMPONENT_STACK_FRAME.test(line));
  return frame?.match(COMPONENT_STACK_FRAME)[1] ?? null;
}

class ComponentErrorBoundary extends React.Component {
  static propTypes = {
    children: PropTypes.node,
  };

  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkLoadError: false, caughtError: null };
  }

  static getDerivedStateFromError(error) {
    if (isResizeObserverLoopError(error)) return { hasError: false, isChunkLoadError: false, caughtError: null };
    if (isChunkLoadError(error)) return { hasError: true, isChunkLoadError: true, caughtError: null };
    return { hasError: true, isChunkLoadError: false, caughtError: error };
  }

  componentDidUpdate(prevProps) {
    const { children } = this.props;
    const { hasError } = this.state;
    if (hasError && prevProps.children !== children) {
      // Route/content changed — give the new tree a clean slate so a chunk-load failure
      // on one navigation doesn't blank out subsequent navigations.
      this.setState({ hasError: false, isChunkLoadError: false, caughtError: null });
    }
  }

  componentDidCatch(error, info) {
    if (isResizeObserverLoopError(error)) return;

    if (isChunkLoadError(error)) {
      dispatchNetworkErrorSnackbar({ force: true });
      return;
    }

    if (process.env.NODE_ENV === 'test') {
      console.log(error);
    }

    // Mutating the caught error (same reference as state.caughtError) rather than calling
    // setState: componentDidCatch runs before the fallback's effects, so InternalError sees
    // the stack without an extra render. Frozen/exotic thrown values just skip the stack.
    try {
      if (error && typeof error === 'object' && !error.componentStack) {
        error.componentStack = info?.componentStack;
      }
    } catch {
      // attribution falls back to the error message
    }

    // Suppress error reporting during the auth-redirect window: render crashes here are
    // expected fallout from the 401/403 that triggered the redirect, not real bugs.
    if (readRedirectFlag()) return;

    const { children } = this.props;
    const componentName =
      getComponentNameFromStack(info?.componentStack) || children?.displayName || children?.name || 'unknown component';
    reportError(`<ComponentErrorBoundary /> caught error in: ${componentName}`, error);
  }

  render() {
    const { children } = this.props;
    const { hasError, isChunkLoadError: chunkLoadError, caughtError } = this.state;

    if (!hasError) return children ?? null;

    // Avoid flashing the maintenance overlay while a logout redirect is in flight.
    if (readRedirectFlag()) return null;

    // Chunk load failures (typically offline) surface as a recoverable snackbar from
    // componentDidCatch — render an empty page so the user can pick another sidebar link.
    if (chunkLoadError) return null;

    return <InternalErrorComponent crashError={caughtError} />;
  }
}

export { ComponentErrorBoundary };
export default ComponentErrorBoundary;
